import type {
  SearchAppsRequest,
  SearchAppsResponse,
  SortType,
} from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/types';
import { EnhancedSearchApps } from './enhanced-search-apps';
import type { SearchAppsService } from '../../app-market-search/search-apps.service';
import type {
  EnhancedSearchAppsResponse,
  EnhancedSearchAppsRequest,
  EnhancedQueryAppsFilter,
} from '../types';
import type { SearchAppsPayload } from '../../app-market-search';
import { APPS_LIMIT } from '../../tag-apps';

export type RequestMethod = <TPayload, TResponse>(params: {
  methodName:
    | 'searchApps'
    | 'getAppsByAppIds'
    | 'getTagSections'
    | 'getDynamicSections';
  payload: TPayload;
}) => Promise<TResponse>;

export class EnhancedSearchHandler {
  constructor(
    private readonly makeRequest: RequestMethod,
    private readonly searchAppsService: SearchAppsService,
    private readonly installedAppsFirstTagIds: Record<string, string>,
  ) {}

  public async handleEnhancedSearchApps(
    request: EnhancedSearchAppsRequest,
  ): Promise<EnhancedSearchAppsResponse> {
    const filter = request.filter || {};
    const { installed, tagId } = filter;
    let installedFilter = installed;

    // Auto-set installed-first for specific tag IDs if no filter is specified
    if (
      tagId &&
      Object.values(this.installedAppsFirstTagIds).includes(tagId) &&
      !installedFilter
    ) {
      installedFilter = 'installed-first';
    }

    const basePayload = this.createBasePayload(request);

    return this.processEnhancedSearch(installedFilter, basePayload, request);
  }

  private async processEnhancedSearch(
    installedFilter: EnhancedQueryAppsFilter['installed'],
    basePayload: SearchAppsRequest,
    request: EnhancedSearchAppsRequest,
  ): Promise<EnhancedSearchAppsResponse> {
    switch (installedFilter) {
      case 'installed':
      case 'not-installed':
        return this.handleInstalledFilter(basePayload, installedFilter);

      case 'installed-first':
        return this.handleInstalledFirstFilter(basePayload, request);

      default:
        return this.handleDefaultSearch(basePayload);
    }
  }

  private createBasePayload(
    request: EnhancedSearchAppsRequest,
  ): SearchAppsRequest {
    const filter = request.filter || {};
    const { isSale, tagId, searchTerm, includeDealerBenefits } = filter;

    return {
      paging: request.paging ?? {
        limit: APPS_LIMIT,
        offset: 0,
      },
      sortTypes: request.sortTypes as SortType[],
      filter: {
        ...(isSale !== undefined ? { isSale } : {}),
        ...(tagId ? { tagId } : {}),
        ...(searchTerm ? { searchTerm } : {}),
        ...(includeDealerBenefits !== undefined
          ? { includeDealerBenefits }
          : {}),
      },
      ...(request.customParams ? { customParams: request.customParams } : {}),
    };
  }

  private async handleInstalledFilter(
    basePayload: SearchAppsRequest,
    installedFilter: 'installed' | 'not-installed',
  ): Promise<EnhancedSearchAppsResponse> {
    const payload = {
      ...basePayload,
      filter: {
        ...basePayload.filter,
        installed: installedFilter === 'installed',
      },
    };

    return this.makeSearchRequest(payload);
  }

  private async handleInstalledFirstFilter(
    basePayload: SearchAppsRequest,
    request: EnhancedSearchAppsRequest,
  ): Promise<EnhancedSearchAppsResponse> {
    const installedPayload = {
      ...basePayload,
      paging: { offset: 0, limit: 100 },
      filter: { ...basePayload.filter, installed: true },
    };

    const nonInstalledPayload = {
      ...basePayload,
      paging: request.paging,
      filter: { ...basePayload.filter, installed: false },
    };

    const [installedApps, nonInstalledApps] = await Promise.all([
      this.makeSearchRequest(installedPayload),
      this.makeSearchRequest(nonInstalledPayload),
    ]);

    return {
      appIds: [...installedApps.appIds, ...nonInstalledApps.appIds],
      hasMoreApps: nonInstalledApps.hasMoreApps,
      totalApps: installedApps.totalApps + nonInstalledApps.totalApps,
    };
  }

  private async handleDefaultSearch(
    basePayload: SearchAppsRequest,
  ): Promise<EnhancedSearchAppsResponse> {
    return this.searchAppsService.apps({
      paging: basePayload.paging,
      filter: basePayload.filter,
      sortTypes: basePayload.sortTypes,
      shouldUseSearchFilter: true,
      ...(basePayload.customParams
        ? { customParams: basePayload.customParams }
        : {}),
    } as SearchAppsPayload);
  }

  private async makeSearchRequest(
    payload: SearchAppsRequest,
  ): Promise<EnhancedSearchAppsResponse> {
    const response = await this.makeRequest<
      SearchAppsRequest,
      SearchAppsResponse
    >({
      methodName: 'searchApps',
      payload,
    });

    return new EnhancedSearchApps(response).toJSON();
  }
}
